import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Box, Button, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useTheme, withStyles } from "@mui/styles";
import AccountIcon from "@iconify/icons-mdi/account";
import CostIcon from "@iconify/icons-mdi/cash";
import ProjectIcon from "@iconify/icons-mdi/chart-gantt";
import CloseIcon from "@iconify/icons-mdi/close";
import ClientIcon from "@iconify/icons-mdi/user-tie";
import { InlineIcon } from "@iconify/react";
import {
  MyTimesheetContractsFilter,
  MyTimesheetFilterItem,
  MyTimesheetFilterSubtype,
} from "my-timesheet/components";
import {
  useMyTimesheetFilterCounterState,
  useMyTimesheetFilterOpenState,
  useMyTimesheetFilterState,
} from "my-timesheet/hooks";
import { ProductsT, RoleCategoriesT } from "oc/configs/constants";
import { useOcRoleFilters } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

const StyledButton = withStyles({
  root: {
    border: "2px solid #212121 !important",
    "&:hover": {
      border: "2px solid #212121",
    },
    "&.Mui-disabled": {
      border: "2px solid #a8aaaa !important",
    },
  },
})(Button);

interface MyTimesheetFilterProps {
  data: any;
  setData: (data: any) => void;
  onApply?: () => void;
  onReset?: () => void;
  onDisabledToTrue?: () => void;
  handleRun?: () => void;
  contract: string;
  product: ProductsT;
  role: RoleCategoriesT;
}

export const MyTimesheetFilter: React.FC<MyTimesheetFilterProps> = ({
  data,
  onApply,
  onReset,
  onDisabledToTrue,
  handleRun,
  contract,
  product,
  role,
}) => {
  const { t } = useTranslation();
  const theme = useTheme<Theme>();
  const isMobile = useOcIsMobile();

  const {
    state: actualFilterState,
    resetState: resetFilterState,
    isTouched,
  } = useMyTimesheetFilterState();

  const {
    roleFilters,
    resetRoleFilters,
    isTouched: isRoleFilterTouched,
  } = useOcRoleFilters({
    contract,
    product,
    role: role,
    id: "MyTimesheetContractsFilter",
  });

  const { resetState: resetOpenedFilterState } =
    useMyTimesheetFilterOpenState();

  const handleResetFilter = () => {
    resetFilterState();
    resetRoleFilters();
    resetOpenedFilterState();
  };

  const [isResetLabel, setIsResetLabel] = React.useState(false);
  const [savedFilterState, setSavedFilterState] = useState<any>(null);
  const [savedRoleFilters, setSavedRoleFilters] = useState<any | null>(null);

  const handleAction = () => {
    if (isResetLabel) {
      handleResetFilter();
      setIsResetLabel(false);
      onReset?.();
    } else {
      if (onApply) onApply();
      else if (handleRun) handleRun();
      setSavedFilterState(actualFilterState);
      setSavedRoleFilters(roleFilters);
      setIsResetLabel(true);
      resetOpenedFilterState();
    }
  };

  const { state: counter } = useMyTimesheetFilterCounterState();

  const disabled = !isTouched && !isRoleFilterTouched;

  const prevDisabledRef = useRef<boolean>(disabled);
  useEffect(() => {
    if (prevDisabledRef.current === false && disabled === true) {
      onDisabledToTrue?.();
    }
    prevDisabledRef.current = disabled;
  }, [disabled, onDisabledToTrue]);

  useEffect(() => {
    if (isResetLabel) {
      if (!_.isEqual(actualFilterState, savedFilterState)) {
        setIsResetLabel(false);
      }
      if (!_.isEqual(roleFilters, savedRoleFilters)) {
        setIsResetLabel(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualFilterState, roleFilters]);

  useEffect(() => {
    if (actualFilterState !== savedFilterState) {
      setSavedFilterState(actualFilterState);
    }
    if (!_.isEqual(roleFilters, savedRoleFilters)) {
      setSavedRoleFilters(roleFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buttonLabel = isMobile ? "" : t("OC_RESET")

  return (
    <Box>
      <Box
        height={isMobile ? "auto" : "56px"}
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="flex-start"
        alignItems="center"
        style={{
          backgroundColor: theme.palette.gray30.main,
          borderRadius: "4px",
          border: `1px solid ${theme.palette.gray60.main}`,
          overflow: "visible",
        }}
      >
        <Box display="flex" flexDirection="row" alignItems="center">
          <Box padding="10px 16px">
            <Typography
              style={{
                fontSize: "14px",
                lineHeight: "normal",
                letterSpacing: "0.75px",
                color: theme.palette.gray60.main,
              }}
            >
              {t("OC_FILTER").toUpperCase()}
            </Typography>
          </Box>
          <Box
            style={{
              width: "1px",
              backgroundColor: theme.palette.gray40.main,
              height: "36px",
            }}
          />
          <MyTimesheetFilterItem
            label={t("OC_EMPLOYEES")}
            icon={AccountIcon}
            type="contract"
            selectedItemsCount={counter["contract"]}
            isMobile={isMobile}
          />
          <Box
            style={{
              width: "1px",
              backgroundColor: theme.palette.gray40.main,
              height: "36px",
            }}
          />
          <MyTimesheetFilterItem
            label={t("MY_TIMESHEET_PROJECT")}
            icon={ProjectIcon}
            selectedItemsCount={counter["project"]}
            type="project"
            isMobile={isMobile}
          />
        </Box>
        <Box
          marginTop={isMobile ? "16px" : "0"}
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box
              style={{
                width: "1px",
                backgroundColor: theme.palette.gray40.main,
                height: "36px",
              }}
            />
            <MyTimesheetFilterItem
              label={t("MY_TIMESHEET_CLIENT")}
              icon={ClientIcon}
              type="client"
              selectedItemsCount={counter["client"]}
              isMobile={isMobile}
            />
            <Box
              style={{
                width: "1px",
                backgroundColor: theme.palette.gray40.main,
                height: "36px",
              }}
            />
            <MyTimesheetFilterItem
              label={t("OC_COST")}
              icon={CostIcon}
              type="cost"
              selectedItemsCount={counter["cost"]}
              isMobile={isMobile}
            />
            <Box
              style={{
                width: "1px",
                backgroundColor: theme.palette.gray40.main,
                height: "36px",
              }}
            />
          </Box>
          <Box marginRight="16px">
            <StyledButton
              id="filter"
              variant="outlined"
              color="secondary"
              disableElevation
              onClick={handleAction}
              disabled={disabled}
              startIcon={
                isResetLabel && (
                  <InlineIcon
                    icon={CloseIcon}
                    color={
                      isTouched || isRoleFilterTouched
                        ? theme.palette.darkBase.main
                        : theme.palette.gray40.main
                    }
                  />
                )
              }
            >
              {isResetLabel ? buttonLabel : t("OC_FILTER")}
            </StyledButton>
          </Box>
        </Box>
      </Box>
      <MyTimesheetContractsFilter data={data} role={role} />
      <MyTimesheetFilterSubtype type="project" />
      <MyTimesheetFilterSubtype type="client" />
      <MyTimesheetFilterSubtype type="cost" />
    </Box>
  );
};

export default MyTimesheetFilter;
